import React, { Suspense, useEffect, useMemo, useState } from "react";
import { MESSAGE } from "../constants";
import {
  buildJournalToolsConfig,
  buildQuestionOnlyParams,
  createRemoteJournalWsol,
  initializeJournalFit,
  loadJournalFitScript,
  resolveHostAuthToken,
} from "../journalWsol";

type JournalLoaderProps = {
  itemId?: string;
  componentId: string;
  env?: string;
  getAuthToken?: (callback: (token: string) => void) => void;
};

export const JournalLoader: React.FC<JournalLoaderProps> = ({
  itemId,
  componentId,
  env,
  getAuthToken,
}) => {
  const [fitInitialized, setFitInitialized] = useState(false);
  const [fitScriptReady, setFitScriptReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const RemoteJournal = useMemo(() => createRemoteJournalWsol(env), [env]);

  const toolParams = useMemo(() => {
    if (!itemId) {
      return null;
    }
    return buildQuestionOnlyParams(itemId, componentId);
  }, [itemId, componentId]);

  useEffect(() => {
    let cancelled = false;

    resolveHostAuthToken(getAuthToken)
      .then((token) => {
        if (!cancelled) {
          setAuthToken(token);
          setAuthResolved(true);
        }
      })
      .catch((authError: Error) => {
        if (!cancelled) {
          setError(authError);
          setAuthResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getAuthToken]);

  useEffect(() => {
    if (!authResolved || !authToken) {
      return;
    }

    let cancelled = false;

    loadJournalFitScript(env)
      .then(() => {
        if (!cancelled) {
          setFitScriptReady(true);
        }
      })
      .catch((loadError: Error) => {
        if (!cancelled) {
          setError(loadError);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authResolved, authToken, env]);

  useEffect(() => {
    if (!fitScriptReady || !authToken) {
      return;
    }

    const toolsConfig = buildJournalToolsConfig(env);
    initializeJournalFit(toolsConfig, setFitInitialized);
  }, [fitScriptReady, authToken, env]);

  if (!itemId || !toolParams) {
    return <div>{MESSAGE.provideItemId}</div>;
  }

  if (error) {
    return <div>Error loading remote component: {error.message}</div>;
  }

  if (!authResolved) {
    return <div>{MESSAGE.loading}</div>;
  }

  if (!authToken) {
    return <div>{MESSAGE.sessionUnavailable}</div>;
  }

  if (!fitScriptReady || !fitInitialized) {
    return (
      <div>{fitScriptReady ? MESSAGE.waitingForFit : MESSAGE.loadingWritingSolution}</div>
    );
  }

  return (
    <Suspense fallback={<div>{MESSAGE.loadingWritingSolution}</div>}>
      <RemoteJournal params={toolParams} />
    </Suspense>
  );
};
